// Account-scoped device records and pending mutations share a single IDB
// transaction. A failed write must never produce a saved confirmation.
export const DEVICE_PROGRESS_DB = "nihongotech-study-progress";
export type StudyData = Record<string, unknown>;
export type Edit =
    | { kind: "set"; key: string; value: unknown }
    | { kind: "setIfMissing"; key: string; value: unknown }
    | { kind: "remove"; key: string }
    | { kind: "increment"; key: string; field?: string }
    | { kind: "reset" };
export type DeviceRecord = {
    userId: string; feature: string; data: StudyData; revision: number;
    cloudUpdatedAt?: string; updatedAt: string; syncedRevision?: number;
};
export type PendingRecord = {
    userId: string; feature: string; revision: number; baseUpdatedAt?: string;
    edits: Array<Edit & { id: string; createdAt: string }>;
};
export type DeviceSnapshot = { record?: DeviceRecord; pending?: PendingRecord; hadPending?: boolean };
let database: Promise<IDBDatabase> | undefined;
const listeners = new Set<(userId: string, feature: string) => void>();
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("nihongotech-study-progress") : null;
channel?.addEventListener("message", event => {
    if (typeof event.data?.userId === "string" && typeof event.data?.feature === "string")
        listeners.forEach(listener => listener(event.data.userId, event.data.feature));
});
function notify(userId: string, feature: string) {
    listeners.forEach(listener => listener(userId, feature));
    channel?.postMessage({ userId, feature });
}
export function subscribeDeviceProgress(listener: (userId: string, feature: string) => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
function openDatabase(): Promise<IDBDatabase> {
    if (!database) database = new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === "undefined") { reject(new Error("Device storage unavailable")); return; }
        const request = indexedDB.open(DEVICE_PROGRESS_DB, 1);
        request.onupgradeneeded = () => {
            for (const name of ["records", "pending"]) request.result.createObjectStore(name, { keyPath: ["userId", "feature"] });
            request.result.createObjectStore("imports", { keyPath: "feature" });
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error("Close other app tabs and retry device storage"));
        request.onsuccess = () => {
            request.result.onversionchange = () => { request.result.close(); database = undefined; };
            resolve(request.result);
        };
    }).catch(error => { database = undefined; throw error; });
    return database;
}
function result<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}
function complete(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onabort = () => reject(transaction.error || new Error("Device save aborted"));
        transaction.onerror = () => { /* abort reports the final failure */ };
    });
}
export async function readDeviceProgress(userId: string, feature: string): Promise<DeviceSnapshot> {
    const db = await openDatabase();
    const transaction = db.transaction(["records", "pending"], "readonly");
    const [record, pending] = await Promise.all([
        result<DeviceRecord | undefined>(transaction.objectStore("records").get([userId, feature])),
        result<PendingRecord | undefined>(transaction.objectStore("pending").get([userId, feature])),
    ]);
    return { record, pending };
}
const safeKey = (key: string) => !["__proto__", "constructor", "prototype"].includes(key);
export function applyEdit(data: StudyData, edit: Edit): StudyData {
    if (edit.kind === "reset") return {};
    if (!safeKey(edit.key) || (edit.kind === "increment" && edit.field && !safeKey(edit.field))) throw new Error("Invalid progress key");
    const next = { ...data };
    if (edit.kind === "remove") delete next[edit.key];
    else if (edit.kind === "set" || edit.kind === "setIfMissing") {
        if (edit.kind === "set" || !Object.prototype.hasOwnProperty.call(data, edit.key)) next[edit.key] = edit.value;
    }
    else {
        const increment = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) + 1 : 1;
        if (edit.field) {
            const current = data[edit.key];
            const fields = current && typeof current === "object" && !Array.isArray(current) ? { ...current as StudyData } : {};
            fields[edit.field] = increment(fields[edit.field]);
            next[edit.key] = fields;
        } else next[edit.key] = increment(data[edit.key]);
    }
    return next;
}

// The reducer runs inside the read/write transaction, so concurrent tabs and
// repeated button presses read the latest committed counts.
async function commitDeviceEdits(userId: string, feature: string, edits: Edit[]): Promise<DeviceSnapshot> {
    const db = await openDatabase();
    const transaction = db.transaction(["records", "pending"], "readwrite");
    const done = complete(transaction);
    let snapshot: DeviceSnapshot = {};
    const recordRequest = transaction.objectStore("records").get([userId, feature]);
    const pendingRequest = transaction.objectStore("pending").get([userId, feature]);
    let ready = 0;
    const write = () => {
        if (++ready !== 2) return;
        try {
            const previous = recordRequest.result as DeviceRecord | undefined;
            const pending = pendingRequest.result as PendingRecord | undefined;
            const now = new Date().toISOString();
            const revision = (previous?.revision || 0) + 1;
            const record: DeviceRecord = { userId, feature, data: edits.reduce(applyEdit, previous?.data || {}), revision, updatedAt: now, cloudUpdatedAt: previous?.cloudUpdatedAt, syncedRevision: previous?.syncedRevision };
            const nextPending: PendingRecord = { userId, feature, revision, baseUpdatedAt: pending?.baseUpdatedAt || previous?.cloudUpdatedAt,
                edits: [...(pending?.edits || []), ...edits.map((edit, index) => ({ ...edit, id: `${revision}:${index}`, createdAt: now }))] };
            transaction.objectStore("records").put(record);
            // Guest records persist locally but never enter an account's queue.
            if (userId !== "guest") transaction.objectStore("pending").put(nextPending);
            snapshot = { record, pending: userId === "guest" ? undefined : nextPending, hadPending: Boolean(pending) };
        } catch { transaction.abort(); }
    };
    recordRequest.onsuccess = write;
    pendingRequest.onsuccess = write;
    await done;
    notify(userId, feature);
    return snapshot;
}

// A cloud response may seed a clean record, but cannot overwrite edits made
// while that response was in flight or a queue retained from an earlier visit.
export async function seedDeviceProgress(userId: string, feature: string, data: StudyData, expectedRevision: number, cloudUpdatedAt?: string): Promise<DeviceSnapshot> {
    const db = await openDatabase();
    const transaction = db.transaction(["records", "pending"], "readwrite");
    const done = complete(transaction);
    const records = transaction.objectStore("records");
    const request = records.get([userId, feature]);
    const pending = transaction.objectStore("pending").get([userId, feature]);
    let remaining = 2;
    const seed = () => {
        if (--remaining) return;
        if (pending.result || (request.result?.revision || 0) !== expectedRevision) return;
        records.put({ userId, feature, data, syncedRevision: request.result?.syncedRevision, revision: expectedRevision + 1, cloudUpdatedAt, updatedAt: new Date().toISOString() });
    };
    request.onsuccess = seed; pending.onsuccess = seed;
    await done;
    notify(userId, feature);
    return readDeviceProgress(userId, feature);
}

export async function acknowledgeDeviceSave(userId: string, feature: string, revision: number, cloudUpdatedAt?: string): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(["pending", "records"], "readwrite");
    const done = complete(transaction);
    const store = transaction.objectStore("pending");
    const request = store.get([userId, feature]);
    request.onsuccess = () => {
        const pending = request.result as PendingRecord | undefined;
        if (!pending) return;
        const edits = pending.edits.filter(edit => Number(edit.id.split(":")[0]) > revision);
        if (!edits.length) store.delete([userId, feature]);
        else store.put({ ...pending, edits, baseUpdatedAt: cloudUpdatedAt || pending.baseUpdatedAt });
    };
    const records = transaction.objectStore("records");
    const record = records.get([userId, feature]);
    record.onsuccess = () => { if (record.result && cloudUpdatedAt) records.put({ ...record.result, cloudUpdatedAt }); };
    await done;
    notify(userId, feature);
}

export async function listPendingProgress(userId: string): Promise<PendingRecord[]> {
    const db = await openDatabase();
    const rows = await result<PendingRecord[]>(db.transaction("pending").objectStore("pending").getAll());
    return rows.filter(row => row.userId === userId);
}

async function commitLegacyProgress(userId: string, feature: string, legacy: StudyData): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(["records", "pending", "imports"], "readwrite");
    const done = complete(transaction);
    const marker = transaction.objectStore("imports").get(feature);
    const old = transaction.objectStore("records").get([userId, feature]);
    const queue = transaction.objectStore("pending").get([userId, feature]);
    let remaining = 3;
    const write = () => {
        if (--remaining || marker.result) return;
        const record = old.result as DeviceRecord | undefined;
        const data = record?.data || {};
        const edits: Edit[] = Object.keys(legacy).filter(key => safeKey(key) && !Object.prototype.hasOwnProperty.call(data, key)).map(key => ({ kind: "setIfMissing", key, value: legacy[key] }));
        const revision = (record?.revision || 0) + 1;
        const now = new Date().toISOString();
        if (edits.length) transaction.objectStore("records").put({ ...record, userId, feature, data: edits.reduce(applyEdit, data), revision, updatedAt: now });
        if (edits.length) transaction.objectStore("pending").put({ userId, feature, revision, baseUpdatedAt: queue.result?.baseUpdatedAt || record?.cloudUpdatedAt,
            edits: [...(queue.result?.edits || []), ...edits.map((edit, index) => ({ ...edit, id: `${revision}:${index}`, createdAt: now }))] });
        transaction.objectStore("imports").put({ feature, userId, importedAt: now });
    };
    marker.onsuccess = write; old.onsuccess = write; queue.onsuccess = write;
    await done;
    notify(userId, feature);
}

export async function closeDeviceProgress(): Promise<void> {
    if (database) (await database).close();
    database = undefined;
}

// Stable per IndexedDB installation, so receipts from one device cannot
// acknowledge another device's edits. Reinstall/eviction creates a new ID.
export async function getProgressDeviceId(): Promise<string> {
    const db = await openDatabase();
    const transaction = db.transaction("imports", "readwrite");
    const done = complete(transaction);
    const store = transaction.objectStore("imports");
    const request = store.get("__sync_device__");
    let deviceId = "";
    request.onsuccess = () => {
        try {
            deviceId = request.result?.deviceId || Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
            if (!request.result) store.put({ feature: "__sync_device__", deviceId });
        } catch { transaction.abort(); }
    };
    await done;
    return deviceId;
}

// Rebase changes made during an upload onto the acknowledged cloud result,
// and trim only the prefix proven saved by this device's durable receipt.
export async function reconcileDeviceSave(userId: string, feature: string, savedRevision: number, data: StudyData, updatedAt?: string): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(["records", "pending"], "readwrite");
    const done = complete(transaction);
    const records = transaction.objectStore("records");
    const pending = transaction.objectStore("pending");
    const row = records.get([userId, feature]);
    const queue = pending.get([userId, feature]);
    let remaining = 2;
    const write = () => {
        if (--remaining) return;
        try {
            if ((row.result?.syncedRevision || 0) >= savedRevision) return;
            const edits = (queue.result?.edits || []).filter((edit: PendingRecord["edits"][number]) => Number(edit.id.split(":")[0]) > savedRevision);
            records.put({ userId, feature, data: edits.reduce(applyEdit, data), revision: (row.result?.revision || 0) + 1, cloudUpdatedAt: updatedAt, syncedRevision: savedRevision, updatedAt: new Date().toISOString() });
            if (edits.length) pending.put({ ...queue.result, edits, baseUpdatedAt: updatedAt });
            else pending.delete([userId, feature]);
        } catch { transaction.abort(); }
    };
    row.onsuccess = write; queue.onsuccess = write;
    await done;
    notify(userId, feature);
}

// Register writes before their first await, so update checks also see saves
// which have not opened their IndexedDB transaction yet.
const activeWrites = new Set<Promise<unknown>>();
function trackWrite<T>(write: Promise<T>): Promise<T> {
    activeWrites.add(write);
    void write.then(() => activeWrites.delete(write), () => activeWrites.delete(write));
    return write;
}
export function saveDeviceEdits(userId: string, feature: string, edits: Edit[]): Promise<DeviceSnapshot> {
    return trackWrite(commitDeviceEdits(userId, feature, edits));
}
export function importLegacyProgress(userId: string, feature: string, legacy: StudyData): Promise<void> {
    return trackWrite(commitLegacyProgress(userId, feature, legacy));
}
export async function canApplyDeviceUpdate(): Promise<boolean> {
    while (activeWrites.size) await Promise.all(Array.from(activeWrites));
    const db = await openDatabase();
    const count = await result(db.transaction("pending").objectStore("pending").count());
    return count === 0 && activeWrites.size === 0;
}

// A redundant marker detects database-only eviction. Complete origin deletion
// removes both markers and cannot be distinguished from a first visit.
export async function inspectDeviceStorage(): Promise<boolean> {
    const key = "nihongotech.storageMarker.v1";
    const db = await openDatabase();
    const transaction = db.transaction("imports", "readwrite");
    const done = complete(transaction);
    const store = transaction.objectStore("imports");
    const request = store.get("__storage_marker__");
    let lost = false;
    request.onsuccess = () => {
        try {
            const previous = localStorage.getItem(key);
            lost = previous === "lost" || Boolean(previous && !request.result);
            store.put({ feature: "__storage_marker__" });
            localStorage.setItem(key, lost ? "lost" : "present");
        } catch { /* Storage APIs can be independently unavailable. */ }
    };
    await done;
    return lost;
}
